import { LAMPORTS_PER_SOL, SystemProgram } from "@solana/web3.js";
import { useFormikContext } from "formik";
import {
  FC,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";
import {
  ADD_SELL_PERMISSION,
  EMPTY_STRING,
} from "../../../../common/constants/common.constants";
import { ClubType } from "../../../../common/enums/club.enum";
import {
  GoveranceType,
  ProposalAction,
} from "../../../../common/enums/proposals.enums";
import { IOption } from "../../../../common/interfaces/common.interface";
import {
  ICreateProposalForm,
  ICreateProposalProps,
  ProposalTypeNew,
} from "../../../../common/interfaces/proposals.interface";
import { clubsStore } from "../../../../contexts/clubStore";

import VotingThreshold from "../../../CreateClubWizard/FinancialConfiguration/VotingThreshold/VotingThreshold";
import WizardField from "../../../WizardField/WizardField";
import RoleVotesConfig from "./RoleVotesConfig/RoleVotesConfig";

const UpdateConfigProposal: FC<{ formConfig: ICreateProposalProps }> = ({
  formConfig,
}) => {
  const { values, setFieldValue } = useFormikContext<ICreateProposalForm>();
  const { treasury, clubBasicInfo } = clubsStore.getState();

  const [isAddingNewSellPermission, toggleIsAddingNewSellPermission] =
    useState(false);

  const updateActiveApprovalQuorum = useCallback(
    (e) => {
      const governances = values.governanceAccounts.map((g) => {
        return {
          ...g,
          isChecked: false,
        };
      });
      if (e.value === ADD_SELL_PERMISSION) {
        toggleIsAddingNewSellPermission(true);
        setFieldValue(formConfig.formFields.activeTradeApprovalQuorum.name, {
          from: EMPTY_STRING,
          to: EMPTY_STRING,
          quorumMinimum: EMPTY_STRING,
          decimals: treasury?.denominatedTokenInfo?.decimals ?? 9,
        });
        setFieldValue(formConfig.formFields.newVotingTime.name, 30);
        setFieldValue(
          formConfig.formFields.proposalType.name,
          ProposalTypeNew.AddSellPermission
        );
        const govIndex = governances.findIndex(
          (g) => g.publickey.toString() === SystemProgram.programId.toString()
        );
        governances[govIndex] = {
          ...governances[govIndex],
          isChecked: true,
          from: EMPTY_STRING,
          newQuorum: EMPTY_STRING,
          publickey: SystemProgram.programId,
          to: EMPTY_STRING,
        };
      } else {
        setFieldValue(
          formConfig.formFields.activeTradeApprovalQuorum.name,
          null
        );
        setFieldValue(
          formConfig.formFields.proposalType.name,
          ProposalTypeNew.UpdateClubConfig
        );
        toggleIsAddingNewSellPermission(false);

        setFieldValue(
          formConfig.formFields.activeTradeApprovalQuorum.name,
          treasury?.sellPermissions[e.value]
        );
        governances[e.value] = { ...governances[e.value], isChecked: true };
      }
      setFieldValue(formConfig.formFields.governanceAccounts.name, governances);
    },
    [values]
  );

  useEffect(() => {
    setupInitialData();
  }, [values.governanceType]);

  const setupInitialData = () => {
    if (
      values.governanceType &&
      values.governanceType.type === GoveranceType.TradeApprovalGovernance
    ) {
      setFieldValue(
        formConfig.formFields.governanceAccounts.name,
        treasury?.sellPermissions.map((spc, index) => {
          return {
            publickey: spc.governance!,
            newQuorum: spc.votingQuorum,
            from: spc.from,
            to: spc.to,
            isChecked: index == 0,
          };
        })
      );
      setFieldValue(formConfig.formFields.newApprovalQuorum.name, null);
    } else {
      setFieldValue(formConfig.formFields.governanceAccounts.name, null);
    }
    if (values.proposalOption !== ProposalAction.UpdateGovernanceConfig) {
      setFieldValue(
        formConfig.formFields.proposalOption.name,
        ProposalAction.UpdateRoleConfig
      );
    }
  };

  const getOptionsForTradeApprovals = useMemo(() => {
    const options: IOption[] = [];
    treasury?.sellPermissions.forEach((sp, index) => {
      if (sp.to > 0) {
        options.push({
          label: `${index + 1}. From ${
            sp.from /
            (treasury.denominatedTokenInfo
              ? Math.pow(10, treasury.denominatedTokenInfo.decimals)
              : LAMPORTS_PER_SOL)
          } ${
            treasury.denominatedTokenInfo
              ? treasury.denominatedTokenInfo.symbol
              : "SOL"
          } to ${
            sp.to /
            (treasury.denominatedTokenInfo
              ? Math.pow(10, treasury.denominatedTokenInfo.decimals)
              : LAMPORTS_PER_SOL)
          } ${
            treasury.denominatedTokenInfo
              ? treasury.denominatedTokenInfo.symbol
              : "SOL"
          } - Percentage needed ${sp.votingQuorum}`,
          value: index.toString(),
        });
      }
    });
    if (
      treasury &&
      treasury.sellPermissions.filter((v) => v.to > 0).length < 10
    ) {
      options.push({
        label: "Add new trade approval quorum",
        value: ADD_SELL_PERMISSION,
      });
    }
    return options;
  }, [values.governanceType]);

  const getGovernanceAccountsForRealm = useMemo(() => {
    const governanceData: IOption[] = [
      {
        label: "Default configuration",
        value: treasury?.governance!.toString()!,
        type: GoveranceType.DefaultGovernance,
      },
    ];
    if (treasury?.withdrawalGovernance) {
      governanceData.push({
        label: "Withdrawal configuration",
        value: treasury.withdrawalGovernance.toString()!,
        type: GoveranceType.WithdrawalGovernance,
      });
    }
    if (treasury?.transferGovernance) {
      governanceData.push({
        label: "Transfer configuration",
        value: treasury.transferGovernance.toString()!,
        type: GoveranceType.TransferGovernance,
      });
    }
    if (treasury?.changeApprovalConfigGovernance) {
      governanceData.push({
        label: "Change club configuration",
        value: treasury.changeApprovalConfigGovernance.toString()!,
        type: GoveranceType.ClubConfigGovernance,
      });
    }
    // syndicate specific we don't have trade proposals
    // if (treasury) {
    //   governanceData.push({
    //     label: "Trade approval configuration",
    //     value: treasury.sellPermissions[0].governance?.toString()!,
    //     type: GoveranceType.TradeApprovalGovernance,
    //   });
    // }

    return governanceData;
  }, [treasury]);

  const handleGovernanceChange = useCallback((e) => {
    const governance = clubBasicInfo.governances.find(
      (g) => g.pubkey.toString() === e.value
    );
    setFieldValue(formConfig.formFields.governanceType.name, e);
    setFieldValue(
      formConfig.formFields.newVotingTime.name,
      ((governance?.account.config.maxVotingTime ?? 0) / 86400).toFixed()
    );
    setFieldValue(
      formConfig.formFields.newApprovalQuorum.name,
      governance?.account.config.voteThresholdPercentage.value
    );
  }, []);

  return (
    <div>
      {values.proposalOption === ProposalAction.UpdateGovernanceConfig && (
        <>
          <WizardField
            type="select"
            name={formConfig.formFields.governanceType.name}
            placeholder={"Governance type"}
            label={formConfig.formFields.governanceType.label}
            selectOptions={getGovernanceAccountsForRealm}
            onChange={(e) => {
              handleGovernanceChange(e);
            }}
          />
          {values.governanceType &&
            values.governanceType.type !==
              GoveranceType.TradeApprovalGovernance && (
              <div className="financial-config__voting">
                <div className="financial-config__voting-input">
                  <WizardField
                    type="number"
                    placeholder="Type voting quorum here"
                    label={formConfig.formFields.newApprovalQuorum.label}
                    name={formConfig.formFields.newApprovalQuorum.name}
                    tooltipTitle={`Minimum percentage (%) of 'accept' votes out of all possible votes`}
                    leftPosition={-8}
                  />
                </div>
                <div className="financial-config__voting-input">
                  <WizardField
                    placeholder="Type max vote time here"
                    type="number"
                    label={formConfig.formFields.newVotingTime.label}
                    name={formConfig.formFields.newVotingTime.name}
                    tooltipTitle="Maximum number of days before proposal is closed"
                    leftPosition={-8}
                  />
                </div>
              </div>
            )}
          {values.governanceType &&
            values.governanceType.type ===
              GoveranceType.TradeApprovalGovernance &&
            values.governanceAccounts && (
              <>
                <WizardField
                  name="trade-approvals"
                  placeholder="Choose trade approval"
                  type="select"
                  selectOptions={getOptionsForTradeApprovals}
                  onChange={(e) => updateActiveApprovalQuorum(e)}
                />
                {values.activeTradeApprovalQuorum && (
                  <div className="trade-approval-config__voting-item">
                    <VotingThreshold
                      name={formConfig.formFields.governanceAccounts.name}
                      valueTo={
                        values.activeTradeApprovalQuorum
                          ? values.activeTradeApprovalQuorum.to
                          : values.governanceAccounts.find((g) => g.isChecked)
                              ?.to
                      }
                      quorumValue={
                        values.governanceAccounts.find((g) => g.isChecked)
                          ?.newQuorum
                      }
                      valueFrom={
                        values.activeTradeApprovalQuorum
                          ? values.activeTradeApprovalQuorum.from
                          : values.governanceAccounts.find((g) => g.isChecked)
                              ?.from
                      }
                      publickey={values.activeTradeApprovalQuorum.governance}
                      mintSymbol={treasury?.denominatedTokenInfo?.symbol!}
                      isChecked
                      areInputsDisabled={!isAddingNewSellPermission}
                      isUpdateGovernanceProposal
                      governance={values.activeTradeApprovalQuorum.governance}
                    />
                  </div>
                )}
              </>
            )}
        </>
      )}
      {values.proposalOption === ProposalAction.UpdateRoleConfig &&
        clubBasicInfo.type === ClubType.RoleBased && (
          <RoleVotesConfig formConfig={formConfig} />
        )}
    </div>
  );
};

export default UpdateConfigProposal;
